import { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import * as signalR from '@microsoft/signalr';
import { API_ENDPOINTS } from '../config';
import { getChatConnection } from '../services/chatConnection';
import './Styles/Chat.css';

const getRawToken = () => {
  const t = localStorage.getItem('JWToken');
  if (!t) return null;
  return t.replace(/^Bearer\s+/i, '').trim();
};

function parseJwt(token) {
  try {
    const payload = token.split('.')[1];
    const jsonPayload = decodeURIComponent(
      atob(payload)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

const normalizeMessage = (m) => {
  if (!m) return null;
  return {
    id: m.id ?? m.Id ?? `${Date.now()}-${Math.random()}`,
    senderUserId: m.senderUserId ?? m.SenderUserId ?? m.senderId ?? m.SenderId ?? null,
    senderName: m.senderName ?? m.SenderName ?? '',
    text: m.text ?? m.Text ?? m.message ?? m.Message ?? '',
    sentAt: m.sentAt ?? m.SentAt ?? m.time ?? m.Time ?? null
  };
};

const formatTime = (dt) => {
  if (!dt) return '';
  try {
    return new Date(dt).toLocaleTimeString();
  } catch {
    return String(dt);
  }
};

function Chat({ title }) {
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [error, setError] = useState('');

  const messagesDivRef = useRef(null);
  const isMounted = useRef(false);
  const seenIds = useRef(new Set());

  const myUserIdRef = useRef(null);

  const loadHistory = async () => {
    const t = getRawToken();
    if (!t) return;

    const res = await fetch(API_ENDPOINTS.CHAT_GLOBAL_HISTORY, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${t}`,
        Accept: 'application/json'
      }
    });

    if (res.status === 204) {
      if (isMounted.current) {
        seenIds.current = new Set();
        setMessages([]);
      }
      return;
    }

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Chat history failed');
    }

    const data = await res.json();
    const list = Array.isArray(data) ? data.map(normalizeMessage).filter(Boolean) : [];

    const nextSeen = new Set();
    for (const msg of list) nextSeen.add(String(msg.id).toLowerCase());

    if (isMounted.current) {
      seenIds.current = nextSeen;
      setMessages(list);
    }
  };

  useEffect(() => {
    isMounted.current = true;

    const t = getRawToken();
    if (t) {
      const payload = parseJwt(t);
      myUserIdRef.current = payload.sub ? String(payload.sub).toLowerCase() : null;
    } else {
      myUserIdRef.current = null;
    }

    let conn = null;

    const onReceiveGlobalMessage = (m) => {
      if (!isMounted.current) return;

      const msg = normalizeMessage(m);
      if (!msg) return;

      const key = String(msg.id).toLowerCase();
      if (seenIds.current.has(key)) return;

      seenIds.current.add(key);
      setMessages(prev => [...prev, msg]);
    };

    const init = async () => {
      const token = getRawToken();
      if (!token) {
        setConnectionStatus('error');
        setError('No token');
        return;
      }

      setError('');
      setConnectionStatus('connecting');

      try {
        conn = await getChatConnection();
        conn.on('ReceiveGlobalMessage', onReceiveGlobalMessage);

        setConnectionStatus(conn.state === signalR.HubConnectionState.Connected ? 'connected' : String(conn.state));
        await loadHistory();
      } catch (e) {
        setConnectionStatus('error');
        setError(e?.message || 'Chat init failed');
      }
    };

    init();

    return () => {
      isMounted.current = false;
      if (conn) {
        try { conn.off('ReceiveGlobalMessage', onReceiveGlobalMessage); } catch { }
      }
    };
  }, []);

  useEffect(() => {
    if (messagesDivRef.current) {
      messagesDivRef.current.scrollTop = messagesDivRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const msg = messageInput.trim();
    if (!msg) return;

    try {
      const conn = await getChatConnection();
      await conn.invoke('SendGlobalMessage', msg);
      setMessageInput('');
      setError('');
    } catch (e) {
      setError('Send failed: ' + (e?.message || 'unknown error'));
    }
  };

  const isMyMessage = (m) => {
    const myId = myUserIdRef.current;
    if (!myId) return false;
    if (!m?.senderUserId) return false;
    return String(m.senderUserId).toLowerCase() === myId;
  };

  const uiTitle = title || 'GLOBAL CHAT';

  const statusKey = String(connectionStatus || '').toLowerCase();
  const statusClass =
    statusKey === 'connected'
      ? 'chat-status chat-status--connected'
      : statusKey === 'connecting' || statusKey === 'reconnecting'
        ? 'chat-status chat-status--connecting'
        : statusKey === 'error'
          ? 'chat-status chat-status--error'
          : 'chat-status';

  const statusText =
    statusKey === 'connected'
      ? 'ONLINE'
      : statusKey === 'connecting' || statusKey === 'reconnecting'
        ? 'CONNECTING'
        : statusKey === 'error'
          ? 'ERROR'
          : String(connectionStatus || 'IDLE').toUpperCase();

  return (
    <Card className="shadow chat-card" style={{ height: '100%', margin: 4 }}>
      <Card.Body className="chat-body">
        <div className="chat-header">
          <div className="chat-header-left">
            <div className="chat-title">{uiTitle}</div>
          </div>
          <div className={statusClass}>{statusText}</div>
        </div>

        {error ? <div className="chat-error">{error}</div> : null}

        <div className="chat-area">
          <div ref={messagesDivRef} className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-message chat-message--system">No messages yet</div>
            ) : (
              messages.map((m) => {
                const isSystem = (!m.senderUserId && (!m.senderName || m.senderName === 'System'));
                const me = !isSystem && isMyMessage(m);

                const classNames = ['chat-message'];
                if (isSystem) classNames.push('chat-message--system');
                else classNames.push(me ? 'chat-message--me' : 'chat-message--other');

                const who = isSystem ? 'System' : (m.senderName || 'Unknown');

                return (
                  <div key={m.id} className={classNames.join(' ')}>
                    {!isSystem ? (
                      <div className="chat-message-meta">
                        <span className="chat-message-who">{who}</span>
                        <span className="chat-message-time">{formatTime(m.sentAt)}</span>
                      </div>
                    ) : null}
                    <div className="chat-message-text">{m.text}</div>
                  </div>
                );
              })
            )}
          </div>

          <div className="chat-input-row">
            <input
              id="messageInput"
              className="message-input"
              autoComplete="off"
              type="text"
              value={messageInput}
              placeholder="Type a message..."
              onChange={e => setMessageInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
              disabled={connectionStatus !== 'connected'}
              style={{ overflow: 'hidden' }}
            />

            <button
              id="sendButton"
              className="send-button send-button--square"
              onClick={handleSend}
              disabled={connectionStatus !== 'connected' || !messageInput.trim()}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default Chat;
